// Generator field rules (TTTL-229): per-field JSON Schema keywords the
// engine merges into the pipeline's schema before generating, keyed by a
// dotted path (`age`, `address.country`, `items[].price` — `[]` steps into
// an array's items). The form edits them as flat rows of path + keyword +
// value; the service carries them grouped by path, one object per field.

export type FieldRuleKeyword =
  | "minimum"
  | "maximum"
  | "minLength"
  | "maxLength"
  | "minItems"
  | "maxItems"
  | "enum"
  | "format";

/** How a keyword's value is typed and parsed. */
export type FieldRuleKind = "number" | "integer" | "list" | "format";

export interface FieldRuleKeywordSpec {
  value: FieldRuleKeyword;
  label: string;
  kind: FieldRuleKind;
}

/** The keywords the engine's generator honours, in the form's order. */
export const FIELD_RULE_KEYWORDS: readonly FieldRuleKeywordSpec[] = [
  { value: "minimum", label: "Minimum", kind: "number" },
  { value: "maximum", label: "Maximum", kind: "number" },
  { value: "minLength", label: "Min length", kind: "integer" },
  { value: "maxLength", label: "Max length", kind: "integer" },
  { value: "minItems", label: "Min items", kind: "integer" },
  { value: "maxItems", label: "Max items", kind: "integer" },
  { value: "enum", label: "Enum", kind: "list" },
  { value: "format", label: "Format", kind: "format" },
];

/** The string formats the generator can fill. */
export const FIELD_RULE_FORMATS = [
  "date",
  "date-time",
  "email",
  "hostname",
  "ipv4",
  "ipv6",
  "objectid",
  "time",
  "url",
  "uuid",
] as const;

export type FieldRuleFormat = (typeof FIELD_RULE_FORMATS)[number];

/** One form row: a field path, the keyword it sets and the typed value. */
export interface FieldRuleRow {
  path: string;
  keyword: FieldRuleKeyword;
  value: string;
}

export const EMPTY_FIELD_RULE: FieldRuleRow = {
  path: "",
  keyword: "minimum",
  value: "",
};

/** The service's shape: keywords per dotted path. */
export type FieldRules = Record<string, Record<string, unknown>>;

// Domain aliases for the strings below (the pipelineLint pattern): they
// name what a string means at a call site without runtime cost.
type FieldPath = string;
type RuleValueText = string;
type KeywordName = string;

export function fieldRuleKind(keyword: FieldRuleKeyword): FieldRuleKind {
  const spec = FIELD_RULE_KEYWORDS.find((k) => k.value === keyword);
  return spec ? spec.kind : "number";
}

// A path is dot-separated segments, each optionally suffixed with `[]`;
// segments carry no whitespace, dots or brackets of their own.
const PATH_SEGMENT = "[^\\s.\\[\\]]+(?:\\[\\])?";
const PATH_PATTERN = new RegExp(`^${PATH_SEGMENT}(?:\\.${PATH_SEGMENT})*$`);

export function fieldRulePathError(path: FieldPath): string | null {
  const trimmed = path.trim();
  if (trimmed === "") return "Add a field path";
  if (!PATH_PATTERN.test(trimmed))
    return "Use dots between fields and [] for array items, e.g. items[].price";
  return null;
}

/** A typed value as the schema carries it, or the reason it can't be. */
type ParsedValue = { value: unknown } | { error: string };

const parseNumber = (text: RuleValueText): ParsedValue => {
  const n = Number(text);
  return Number.isFinite(n) ? { value: n } : { error: "Enter a number" };
};

const parseInteger = (text: RuleValueText): ParsedValue =>
  /^\d+$/.test(text)
    ? { value: Number(text) }
    : { error: "Enter a whole number of 0 or more" };

const parseList = (text: RuleValueText): ParsedValue => {
  const items = text
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");
  return items.length > 0
    ? { value: items }
    : { error: "List the allowed values, separated by commas" };
};

const parseFormat = (text: RuleValueText): ParsedValue =>
  (FIELD_RULE_FORMATS as readonly string[]).includes(text)
    ? { value: text }
    : { error: "Pick one of the supported formats" };

const VALUE_PARSERS: Record<
  FieldRuleKind,
  (text: RuleValueText) => ParsedValue
> = {
  number: parseNumber,
  integer: parseInteger,
  list: parseList,
  format: parseFormat,
};

// Parses one keyword's typed value by its kind.
function parseRuleValue(kind: FieldRuleKind, raw: RuleValueText): ParsedValue {
  const text = raw.trim();
  if (text === "") return { error: "Add a value" };
  return VALUE_PARSERS[kind](text);
}

/** A row with nothing typed — dropped from the payload, never an error. */
export function isBlankFieldRule(row: FieldRuleRow): boolean {
  return row.path.trim() === "" && row.value.trim() === "";
}

// The row parsed as one rule: its path and the value the keyword carries,
// or the first thing wrong with it.
function parseRule(
  row: FieldRuleRow,
): { path: string; value: unknown } | { error: string } {
  const pathError = fieldRulePathError(row.path);
  if (pathError) return { error: pathError };
  const parsed = parseRuleValue(fieldRuleKind(row.keyword), row.value);
  if ("error" in parsed) return parsed;
  return { path: row.path.trim(), value: parsed.value };
}

/** A row's live verdict: null when it would submit as written, the
 *  message to show otherwise. A wholly empty row is fine — it is dropped. */
export function fieldRuleRowError(row: FieldRuleRow): string | null {
  if (isBlankFieldRule(row)) return null;
  const parsed = parseRule(row);
  return "error" in parsed ? parsed.error : null;
}

// Files one parsed rule under its path; a path sets each keyword once.
function addRule(
  fields: FieldRules,
  path: FieldPath,
  keyword: FieldRuleKeyword,
  value: unknown,
): void {
  const keywords = fields[path] ?? {};
  if (keyword in keywords) {
    throw new Error(`Field rule '${path}': ${keyword} is set twice`);
  }
  keywords[keyword] = value;
  fields[path] = keywords;
}

/** Groups the form rows into the service's `fields`, or undefined when no
 *  row carries a rule. Wholly empty rows are skipped; anything else must
 *  parse, and a path may set each keyword once. Throws with the message
 *  the form should show. */
export function buildFieldRules(rows: FieldRuleRow[]): FieldRules | undefined {
  const fields: FieldRules = {};
  for (const row of rows.filter((r) => !isBlankFieldRule(r))) {
    const parsed = parseRule(row);
    if ("error" in parsed) {
      const name = row.path.trim() || row.keyword;
      throw new Error(`Field rule '${name}': ${parsed.error}`);
    }
    addRule(fields, parsed.path, row.keyword, parsed.value);
  }
  return Object.keys(fields).length > 0 ? fields : undefined;
}

// Keywords read in the form's order; anything the engine adds later that
// the form doesn't know sorts after them, alphabetically.
const KEYWORD_ORDER = new Map(FIELD_RULE_KEYWORDS.map((k, i) => [k.value, i]));

function compareKeywords(a: KeywordName, b: KeywordName): number {
  const ia = KEYWORD_ORDER.get(a as FieldRuleKeyword) ?? KEYWORD_ORDER.size;
  const ib = KEYWORD_ORDER.get(b as FieldRuleKeyword) ?? KEYWORD_ORDER.size;
  return ia - ib || a.localeCompare(b);
}

/** One line per ruled path for the detail page: "minimum 21, maximum 60".
 *  Paths sort alphabetically and keywords in the form's order — the
 *  service's maps come back in whatever order the engine serialized them,
 *  which must not reshuffle the page between loads. */
export function describeFieldRules(
  fields: FieldRules | undefined,
): Array<{ path: string; text: string }> {
  if (!fields) return [];
  return Object.entries(fields)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([path, keywords]) => ({
      path,
      text: Object.entries(keywords)
        .sort(([a], [b]) => compareKeywords(a, b))
        .map(([keyword, value]) => `${keyword} ${formatRuleValue(value)}`)
        .join(", "),
    }));
}

function formatRuleValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(" | ");
  return String(value);
}
